import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import axios from "axios";
import { API } from "@/App";
import logger from "@/utils/logger";
import { Button } from "@/components/ui/button";
import GalleryItem from "./gallery/GalleryItem";

const GalleryPage = () => {
  const { t } = useTranslation();
  const statsRef = useScrollReveal();
  const ctaRef = useScrollReveal();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchGallery = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      logger.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const eventTypes = useMemo(() => [
    { value: "all", label: t("gallery.filters.all") },
    { value: "wedding", label: t("gallery.filters.weddings") },
    { value: "corporate", label: t("gallery.filters.corporate") },
    { value: "traditional", label: t("gallery.filters.traditional") },
    { value: "private", label: t("gallery.filters.private") },
  ], [t]);

  const filteredGallery =
    filter === "all"
      ? gallery
      : gallery.filter((item) => item.event_type === filter);

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-[#F4F1DE]">
      {/* Hero Section */}
      <section
        className="pt-32 pb-20 bg-[#3D405B] relative overflow-hidden"
        data-testid="gallery-hero"
      >
        <div className="absolute inset-0 opacity-10 pattern-bg"></div>
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <span className="inline-block bg-[#F2CC8F]/20 text-[#F2CC8F] px-4 py-2 rounded-full text-sm font-medium mb-6">
            {t("gallery.hero.badge")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-['Playfair_Display']">
            {t("gallery.hero.title")}
          </h1>
          <p className="text-xl text-[#F4F1DE]/80 max-w-2xl mx-auto">
            {t("gallery.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white sticky top-16 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div
            className="flex flex-wrap justify-center gap-3"
            data-testid="gallery-filters"
          >
            {eventTypes.map((type) => (
              <Button
                key={type.value}
                variant={filter === type.value ? "default" : "outline"}
                onClick={() => setFilter(type.value)}
                data-testid={`filter-${type.value}`}
                className={`rounded-full ${
                  filter === type.value
                    ? "bg-[#E07A5F] hover:bg-[#D0694E] text-white"
                    : "border-[#E07A5F]/30 text-[#3D405B] hover:bg-[#E07A5F]/10"
                }`}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16" data-testid="gallery-grid-section">
        <div className="max-w-7xl mx-auto px-6">
          {(() => {
            if (loading) {
              return (
                <div className="flex justify-center py-20">
                  <div className="spinner"></div>
                </div>
              );
            }
            if (filteredGallery.length === 0) {
              return (
                <div className="text-center py-20">
                  <p className="text-[#6B705C] text-lg">
                    {t("gallery.noImages")}
                  </p>
                </div>
              );
            }
            return (
            <div className="gallery-grid" data-testid="gallery-grid">
              {filteredGallery.map((item, idx) => (
                <GalleryItem
                  key={item.id}
                  item={item}
                  idx={idx}
                  onSelect={setSelectedImage}
                />
              ))}
            </div>
            );
          })()}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white" data-testid="gallery-stats" ref={statsRef}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: "30+", label: t("gallery.stats.eventsCatered"), id: "events" },
              { number: "3,000+", label: t("gallery.stats.happyGuests"), id: "guests" },
              { number: "10+", label: t("gallery.stats.weddingCelebrations"), id: "weddings" },
              { number: "4+", label: t("gallery.stats.yearsExcellence"), id: "years" },
            ].map((stat, idx) => (
              <div key={stat.id} data-testid={`stat-${stat.id}`} className={`reveal stagger-${idx + 1}`}>
                <div className="text-4xl md:text-5xl font-bold text-[#E07A5F] mb-2">
                  {stat.number}
                </div>
                <div className="text-[#6B705C]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#E07A5F]" data-testid="gallery-cta" ref={ctaRef}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="reveal text-4xl font-bold text-white mb-4 font-['Playfair_Display']">
            {t("gallery.cta.title")}
          </h2>
          <p className="reveal stagger-1 text-white/90 text-lg mb-8 max-w-xl mx-auto">
            {t("gallery.cta.subtitle")}
          </p>
          <div className="reveal stagger-2">
          <Button
            className="bg-white text-[#E07A5F] hover:bg-[#F4F1DE] text-lg px-10 py-6 rounded-full font-medium btn-hover-pulse"
            data-testid="gallery-cta-btn"
            onClick={() => (window.location.href = "/booking")}
          >
            {t("gallery.cta.btn")}
          </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
